/** Matrix B2C Hydrogen application: consumer routes over the existing typed surface protocol. */

import type { TemplateInstance, UnmountFn } from 'hydrogen/runtime';
import { toast } from 'hydrogen/operator';
import { html } from 'hydrogen/runtime';

import type { SurfaceSnapshot } from '../generated/surface.js';
import { accountRequest, accountUnreachableMessage } from './account-api.js';
import {
  BETA_SITE_ACCOUNTS,
  projectMemberships,
  signedInAccount,
  siteAccounts,
  type SignedInAccount,
  type SiteAccounts,
} from './account-session.js';
import {
  createSurfaceClient,
  SurfaceAuthenticationError,
  SurfaceNotFoundError,
} from './api.js';
import { requestedChangeView } from './change-view.js';
import { ConsumerHome } from './consumer-home.js';
import { ConsumerProjects, NoProjects } from './consumer-projects.js';
import type { ReplayDetailView } from './consumer-replay.js';
import { requestedInsightsShow } from './insights-show.js';
import { requestedChangeId, type ChangeDetailView } from './inspector.js';
import {
  changeIdFromPath,
  currentProject,
  isChangeDetailPath,
  NAV,
  parseProjectPath,
  PRODUCT_HOME_PATH,
  PROFILE_PATH,
  PROJECT_TABS,
  projectHref,
  PROJECTS_PATH,
  REPLAY_PATH,
  setCurrentProject,
  SETTINGS_PATH,
  shouldInterceptNavigation,
  type ProjectRoute,
  type ProjectTab,
} from './nav.js';
import { NotFoundBody, NotFoundPage } from './not-found.js';
import { isPublicPath, LANDING_PATH, SIGN_IN_PATH } from './public-paths.js';
import { indexSnapshot } from './route-search.js';
import { createChrome } from './chrome.js';
import type { Palette, PaletteEntry } from './command-palette.js';
import { ActionButton } from './kit.js';
import { connectingBody, ErrorState, LoadingState, NO_PROJECT_MESSAGE } from './states.js';
import { Shell } from './shell.js';
import type { SurfaceConnectionState } from './surface-connection.js';
import type { FrontierController } from './frontier-manifold.js';
import type { GraphCanvasController, GraphCanvasKind } from './graph-canvas.js';
import { lensById, type LensId } from './lenses/catalog.js';
import type { LensController } from './lenses/controller.js';
import type { CodeView } from './project-code.js';
import { projectEntries, viewedProject, type ProjectDirectory } from './project-directory.js';
import { ProjectPage, type ProjectPageParts, type ProjectTabModule } from './project-page.js';
import type { PublicHost, PublicSiteController } from './public-route.js';
import type { CodeController } from './tab-code.js';
import { clearSessionCache, readSessionCache, writeSessionCache } from './session-cache.js';

export type MountedRoot = { instance: TemplateInstance; unmount: UnmountFn };

/**
 * The global destinations the shell draws itself. Settings and Profile are the
 * account's own pages and are fetched on first visit (`route-account.ts`);
 * everything about one project is a tab under `/p/<project>` (see
 * `nav.ts#parseProjectPath`), a chunk of its own per tab (`tab-*.ts`).
 */
export const SHELL_ROUTES: Readonly<
  Record<
    string,
    // `directory` absent: an origin that lists no projects, serving the one its snapshot names.
    (snapshot: SurfaceSnapshot, connection: SurfaceConnectionState, directory?: ProjectDirectory) => TemplateInstance
  >
> = {
  [PRODUCT_HOME_PATH]: ConsumerHome,
  [PROJECTS_PATH]: ConsumerProjects,
};

/** What the Code tab is drawn from until its code is here and a read has been asked for. */
const NO_CODE_VIEW: CodeView = { root: null, files: null, path: '', file: null };

/** What to say when the request never reached an answer. */
/**
 * What went wrong, said to the person rather than to the console.
 *
 * The surface used to print `reason.message` straight through, so a dropped
 * connection reached the page as "Failed to fetch" — the browser's words for
 * its own internals, which name neither what failed nor what to do about it.
 */
export function surfaceUnreachableMessage(reason: unknown): string {
  /*
   * A refused credential said "surface credential refused (401)" — the
   * client's own words for its own internals, on a page a signed-in person is
   * looking at. The bearer that was refused is the website's, not theirs, so
   * the sentence has to name whose problem it is and stop them retyping a
   * password that was already accepted.
   */
  if (reason instanceof SurfaceAuthenticationError) {
    if (reason.reason === 'noProject') return NO_PROJECT_MESSAGE;
    return reason.reason === 'refused'
      ? 'You are signed in, but the authority refused this website’s credential, so no projection can be read. That is a deployment fault, not a password: retry once it is repaired.'
      : 'This browser holds no Matrix session. Sign in to read the authority.';
  }
  if (reason instanceof DOMException && reason.name === 'TimeoutError') {
    return 'The authority did not answer in time. It may be busy; retry the connection.';
  }
  const raw = reason instanceof Error ? reason.message : '';
  if (/failed to fetch|networkerror|load failed/i.test(raw)) {
    return 'The authority is unreachable from this browser. Check that it is serving, then retry.';
  }
  if (/\b5\d\d\b/.test(raw)) {
    return `The authority refused the request: ${raw}. Retrying may succeed once it recovers.`;
  }
  return raw.length > 0 ? raw : 'The authority did not return a surface projection.';
}

/** What a failed snapshot read means, given whether one is already held. */
export type SnapshotFailure = 'reconnect' | 'authenticate' | 'refuse';

/**
 * A refresh that fails while a projection is on screen is not nothing.
 *
 * The read used to return whenever a snapshot was already held, which
 * swallowed every later failure: a session that expired mid-visit went
 * unnoticed, so the operator kept reading authority state they were no longer
 * authorized for, and no indicator moved. A credential failure is decided the
 * same way whether or not a projection is held, because expiry is exactly the
 * case where one is.
 */
export function snapshotFailureAction(reason: unknown, hasSnapshot: boolean): SnapshotFailure {
  /*
   * Only a missing session is answered by signing in. The website Worker holds
   * the authority credential and attaches it server-side, so a 401 that does
   * not say `no_session` is the authority refusing the *site's* bearer while
   * this browser's session is good. Treating that as "authenticate" sent a
   * person who had just been authenticated back to the sign-in form, wiping
   * the page they had reached and offering the one remedy that cannot work.
   *
   * `no_project` is neither: the session is good and the site's credential
   * is fine, but this account may not read the project asked for. Signing in
   * again or waiting will not change that, so it is said, not retried.
   */
  if (reason instanceof SurfaceAuthenticationError) {
    if (reason.reason === 'noProject') return 'refuse';
    return reason.reason === 'refused' ? (hasSnapshot ? 'reconnect' : 'refuse') : 'authenticate';
  }
  return hasSnapshot ? 'reconnect' : 'refuse';
}

/**
 * Whether a read of project `key` may start. One that already failed is not
 * repeated by anything but a person asking ("Try again"): a refused read that
 * is retried by every later trigger is a loop, not a recovery.
 */
export function mayStartRead(failedFor: string | null, key: string, forced: boolean): boolean {
  return forced || failedFor !== key;
}

const STREAM_RECOVERY_BASE_MS = 1_000;
const STREAM_RECOVERY_MAX_MS = 30_000;

/**
 * How long the first projection may take before the page says the project's
 * data is not available. The authority behind a deployment may not exist yet;
 * waiting on it is never an answer, so "Connecting" is bounded.
 */
export const UNAVAILABLE_AFTER_MS = 10_000;

/** What the unavailable state says when the read neither failed nor finished. */
export const UNAVAILABLE_DETAIL =
  'Matrix has not received a certified state for this project from its authority. Nothing is wrong with your account.';

/** Backoff before the Nth attempt to recover a snapshot stream the browser closed. */
export function streamRecoveryDelay(attempt: number): number {
  return Math.min(STREAM_RECOVERY_MAX_MS, STREAM_RECOVERY_BASE_MS * 2 ** Math.max(0, attempt));
}

export function idleChangeDetailView(): ChangeDetailView {
  return { detail: null, status: 'idle', error: null };
}

export function resolveInspectorChangeId(
  requestedId: string | null,
  recentChanges: readonly { id: string }[],
): string | null {
  return requestedId ?? recentChanges.at(-1)?.id ?? null;
}

function locationPathname(): string {
  return window.location.pathname === '' ? '/' : window.location.pathname;
}

/** True when this path names a surface Matrix serves. */
export function isKnownRoute(path: string): boolean {
  return isPublicPath(path) || NAV.some((item) => item.href === path) || parseProjectPath(path) !== null;
}

/** The lens a project route shows: Active Work on Work, and two Insights views. */
export function lensForRoute(route: ProjectRoute | null, search: string): LensId | null {
  if (route === null || route.changeId !== null || route.sub !== null) return null;
  if (route.tab === 'work') return 'work';
  if (route.tab !== 'insights') return null;
  const show = requestedInsightsShow(search);
  if (show === 'pressure') return 'architecture';
  return show === 'frontier-lens' ? 'frontier' : null;
}

/** Whether a project route shows the Frontier manifold (Insights › Frontier). */
function showsManifold(route: ProjectRoute | null, search: string): boolean {
  return route?.tab === 'insights' && route.sub === null && requestedInsightsShow(search) === 'frontier';
}

/**
 * Where a route family's code is, for routes that fetch it on first visit.
 *
 * What first paint of the signed-in shell needs is the entry; everything else
 * is a route family and its own chunk -- the public site, Replay, the account's
 * pages, each project tab, the lenses, the manifold, the graph canvases -- so
 * `/home` downloads none of what it never shows. A family is fetched once;
 * `settled` re-renders so the route that asked for it draws it, and only if
 * that route is still the one on screen -- every mount happens inside a render
 * of the current path, so a person who navigated away before the chunk arrived
 * never gets it mounted.
 */
export type LazyModule<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; module: T }
  | { status: 'failed'; reason: string };

/** The current state of one lazily fetched route family, starting the fetch on first ask. */
export function lazyModule<T>(load: () => Promise<T>, settled: () => void): () => LazyModule<T> {
  let state: LazyModule<T> = { status: 'idle' };
  return () => {
    if (state.status !== 'idle') return state;
    state = { status: 'loading' };
    load()
      .then(
        (module) => {
          state = { status: 'ready', module };
        },
        (reason: unknown) => {
          state = { status: 'failed', reason: reason instanceof Error ? reason.message : String(reason) };
        },
      )
      .finally(settled);
    return state;
  };
}

/** Why a route family's code could not be fetched, or null while it is on its way or here. */
export function codeFailure(loaded: LazyModule<unknown>): string | null {
  return loaded.status === 'failed' ? loaded.reason : null;
}

/**
 * The body a route answers with while its code is fetched, or once fetching it failed.
 *
 * A failed chunk is almost always a deploy that replaced it under an open tab,
 * and a fresh document names the new one -- so the action is a reload, not a
 * retry of the same missing address.
 */
export function surfaceCodeBody(title: string, failure: string | null): TemplateInstance {
  if (failure === null) {
    return LoadingState({ title: `Loading ${title}`, detail: "Fetching this surface's code." });
  }
  return ErrorState({
    id: 'surface-code-error-title',
    title: `${title} could not load`,
    detail: failure,
    action: ActionButton({ action: 'reload', label: 'Reload Matrix', variant: 'outline' }),
  });
}

/** Where to send a redirect that names no served route. Never the address bar. */
export function redirectFallbackPath(path: string): string {
  return isKnownRoute(path) ? path : '/';
}

/** Accept only a known same-origin product route after authentication. */
export function safeRedirectPath(requested: string | null, origin: string): string {
  let destination: URL;
  try {
    destination = new URL(requested ?? PRODUCT_HOME_PATH, origin);
  } catch {
    return PRODUCT_HOME_PATH;
  }
  if (destination.origin !== origin) return PRODUCT_HOME_PATH;
  const path = redirectFallbackPath(destination.pathname);
  return isPublicPath(path) ? PRODUCT_HOME_PATH : `${path}${destination.search}`;
}

export function changeDetailViewFromFailure(reason: unknown): ChangeDetailView {
  // A 400 names an address that cannot be a change, so it is as missing as a
  // 404; neither is a failure that a retry could repair.
  if (
    reason instanceof SurfaceNotFoundError ||
    (reason instanceof Error && /\b40[04]\b/.test(reason.message))
  ) {
    return { detail: null, status: 'missing', error: null };
  }
  return {
    detail: null,
    status: 'error',
    error: surfaceUnreachableMessage(reason),
  };
}

export function loadingChangeDetailView(
  current: ChangeDetailView,
  id: string,
): ChangeDetailView {
  const retained = current.detail?.change.id === id ? current.detail : null;
  return { detail: retained, status: 'loading', error: null };
}

export function shouldSkipChangeDetailFetch(
  status: ChangeDetailView['status'],
  currentId: string | null | undefined,
  requestedId: string,
  inFlightId: string | null,
): boolean {
  return (
    (status === 'ready' && currentId === requestedId) ||
    (status === 'loading' && inFlightId === requestedId)
  );
}

export function reconcileRoot(
  root: Element,
  current: MountedRoot | undefined,
  next: TemplateInstance,
): MountedRoot {
  if (current?.instance.template === next.template) {
    current.instance.update(next.bindings);
    return current;
  }
  current?.unmount();
  return { instance: next, unmount: next.mount(root) };
}

/** Insights › Semantic graph, where the explorer canvas is drawn. */
function isGraphPath(path: string): boolean {
  const route = parseProjectPath(path);
  return route?.tab === 'insights' && requestedInsightsShow(window.location.search) === 'graph';
}

function isChangeGraphPath(path: string): boolean {
  return isChangeDetailPath(path) && requestedChangeView() === 'graph';
}

/** Where the command palette can go. Every entry is a destination; none runs anything. */
function paletteEntries(snapshot: SurfaceSnapshot | null, directory: ProjectDirectory): PaletteEntry[] {
  const project = currentProject();
  const global = NAV.filter((item) => item.href !== REPLAY_PATH).map((item) => ({
    id: item.href,
    label: item.label,
    hint: item.href,
    group: 'Go to',
    href: item.href,
  }));
  const tabs =
    project === null
      ? []
      : PROJECT_TABS.map((item) => ({
          id: `tab:${item.tab}`,
          label: item.label,
          hint: decodeURI(projectHref(item.tab)),
          group: project,
          href: projectHref(item.tab),
        }));
  const projects = projectEntries(directory, snapshot)
    .filter((entry) => entry.scope !== project)
    .map((entry) => ({
      id: `project:${entry.scope}`,
      label: entry.scope,
      hint: 'Open project',
      group: 'Projects',
      href: projectHref('overview', {}, entry.scope),
    }));
  const entities = snapshot
    ? indexSnapshot(snapshot).slice(0, 24).map((hit) => ({
        id: `${hit.kind}:${hit.id}`,
        label: hit.label,
        hint: hit.kind,
        group: 'Authority',
        href: hit.href,
      }))
    : [];
  return [...global, ...tabs, ...projects, ...entities];
}

/** Mounts the B2C Matrix experience while preserving snapshot/SSE authority semantics. */
export function startControlPlane(root: Element): UnmountFn {
  const surface = createSurfaceClient();
  let snapshot: SurfaceSnapshot | null = null;
  /* Who the Worker says this browser is. Null until `/auth/session` answers. */
  let account: SignedInAccount | null = null;
  let error: string | null = null;
  let connectionState: SurfaceConnectionState = 'connecting';
  let mounted: MountedRoot | undefined;
  let graphCanvas: GraphCanvasController | undefined;
  let snapshotController: AbortController | undefined;
  let detailController: AbortController | undefined;
  let detailView = idleChangeDetailView();
  let detailInFlightId: string | null = null;
  /** The change the authority last said does not exist, so it is not asked again. */
  let detailMissingId: string | null = null;
  let replayController: AbortController | undefined;
  let replayView: ReplayDetailView = { status: 'idle' };
  let snapshotRequest = 0;
  let disposed = false;
  /** What `/auth/session` says this deployment's account service does. */
  let site: SiteAccounts = BETA_SITE_ACCOUNTS;
  /** The account's projects as `/auth/session` lists them; null where it lists none. */
  let directory: ProjectDirectory = null;
  /**
   * Whether `/auth/session` has answered for the current session. Off a
   * project's address it decides which project is read, so nothing is read
   * before it answers.
   */
  let sessionAnswered = false;
  /** The public site and its forms, once a public address has fetched them. */
  let publicSite: PublicSiteController | undefined;
  /** The session expired under a signed-in page: the sign-in card says so when it is next drawn. */
  let sessionEnded = false;
  let palette: Palette | undefined;
  /** The first projection has not arrived in `UNAVAILABLE_AFTER_MS`: say so rather than keep connecting. */
  let unavailable = false;
  let unavailableTimer: number | undefined;
  /** The project whose read last failed; nothing re-reads it until "Try again" (or another project). */
  let readFailedFor: string | null = null;
  const chrome = createChrome({ navigate: (href) => navigate(href), signOut: () => void signOut() });
  let stopSnapshots: UnmountFn | undefined;
  let streamRecoveryAttempt = 0;
  let streamRecoveryTimer: ReturnType<typeof setTimeout> | undefined;
  let frontier: FrontierController | undefined;
  let lens: LensController | undefined;
  let lensId: LensId | undefined;
  let lensHost: HTMLElement | undefined;
  /** The Code tab's loader for the session, made once its code is here: file lists and contents are read once per state. */
  let code: CodeController | undefined;

  const afterLocationChange = (): void => {
    render();
    if (!isPublicPath(locationPathname())) {
      if (account === null || !sessionAnswered) {
        sessionAnswered = false;
        void loadAccount();
      }
      syncSurfaceProject();
    }
    void loadChangeDetail();
    root.querySelector<HTMLElement>('#matrix-content')?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  const navigate = (href: string): void => {
    palette?.close();
    window.history.pushState({}, '', href);
    afterLocationChange();
  };

  /** The palette's code (Command, Dialog) is fetched the first time it is asked for. */
  const openPalette = async (): Promise<void> => {
    if (palette === undefined) {
      const loaded = await import('./command-palette.js');
      if (disposed) return;
      palette ??= loaded.createPalette(navigate);
    }
    palette.setEntries(paletteEntries(snapshot, directory));
    palette.open();
  };

  /** Start counting toward "the project's data is not available"; one count at a time. */
  const armUnavailable = (): void => {
    window.clearTimeout(unavailableTimer);
    unavailable = false;
    unavailableTimer = window.setTimeout(() => {
      unavailableTimer = undefined;
      if (disposed || snapshot !== null) return;
      unavailable = true;
      /*
       * A read that has not answered by the bound is abandoned, not left
       * pending behind a notice that says it is over: the request is aborted
       * and the failure latched, so only "Try again" asks again. Aborting is
       * not a failure to the read's own catch (`request` no longer matches).
       */
      if (snapshotController !== undefined) {
        snapshotRequest += 1;
        snapshotController.abort();
        snapshotController = undefined;
        readFailedFor = surface.project() ?? '';
      }
      render();
    }, UNAVAILABLE_AFTER_MS);
  };

  const disarmUnavailable = (): void => {
    window.clearTimeout(unavailableTimer);
    unavailableTimer = undefined;
    unavailable = false;
  };

  const graphCode = lazyModule(() => import('./graph-canvas.js'), () => render());

  /** The graph canvases are drawn by hand once the page holding one has rendered; their code is fetched when one is on screen. */
  const observeGraph = (): void => {
    const path = locationPathname();
    const wanted: GraphCanvasKind | null = !snapshot
      ? null
      : isGraphPath(path)
        ? 'explorer'
        : isChangeGraphPath(path)
          ? 'change'
          : null;
    if (wanted === null) {
      graphCanvas?.observe(null);
      return;
    }
    const loaded = graphCode();
    if (loaded.status !== 'ready') return;
    graphCanvas ??= loaded.module.createGraphCanvas({
      root,
      snapshot: () => snapshot,
      detail: () => detailView,
      render: () => render(),
    });
    graphCanvas.observe(wanted);
  };

  /*
   * The two route families that are most of the client, fetched on first visit
   * (see `lazyModule`). `render` is called when either settles, so the route on
   * screen at that moment -- and only that one -- draws what arrived.
   */
  const frontierCode = lazyModule(async () => {
    const [manifold, view] = await Promise.all([import('./frontier-manifold.js'), import('./manifold-view.js')]);
    return { mountFrontier: manifold.mountFrontier, ManifoldView: view.ManifoldView };
  }, () => render());
  const lensCode = lazyModule(() => import('./lenses/controller.js'), () => render());

  /*
   * The rest of the client, a chunk per route family: Replay, the account's own
   * pages, the public site, and one per project tab. Each is asked for as soon
   * as the address names it -- with the projection's read, not after it.
   */
  const replayCode = lazyModule(() => import('./consumer-replay.js'), () => render());
  const accountCode = lazyModule(() => import('./route-account.js'), () => render());
  const publicCode = lazyModule(() => import('./public-route.js'), () => render());
  const codeTab = lazyModule(() => import('./tab-code.js'), () => render());
  const projectTabs: Record<ProjectTab, () => LazyModule<ProjectTabModule>> = {
    overview: lazyModule(() => import('./tab-overview.js'), () => render()),
    code: codeTab,
    changes: lazyModule(() => import('./tab-changes.js'), () => render()),
    work: lazyModule(() => import('./tab-work.js'), () => render()),
    checks: lazyModule(() => import('./tab-checks.js'), () => render()),
    insights: lazyModule(() => import('./tab-insights.js'), () => render()),
    settings: lazyModule(() => import('./tab-settings.js'), () => render()),
  };

  /** What a form on a public page changes in this shell. */
  const publicHost: PublicHost = {
    render: () => render(),
    navigate: (href) => navigate(href),
    site: () => site,
    leaveSession: () => {
      stopSnapshots?.();
      stopSnapshots = undefined;
      // Another account may be about to answer; the last one's copy must not paint for it.
      clearSessionCache();
      directory = null;
    },
    enterSession: (next) => {
      replayView = { status: 'idle' };
      account = next;
      // A new session: its projects are not the ones the last `/auth/session` listed.
      sessionAnswered = false;
    },
    continueAfterSignIn: () => {
      const requested = new URLSearchParams(window.location.search).get('next');
      navigate(safeRedirectPath(requested, window.location.origin));
    },
  };

  /** Insights › Frontier: the manifold, once its code is here. */
  const manifoldBody = (): TemplateInstance => {
    const loaded = frontierCode();
    if (loaded.status !== 'ready') return surfaceCodeBody('Frontier Manifold', codeFailure(loaded));
    frontier ??= loaded.module.mountFrontier(root, surface, render);
    // The controller owns the canvas and the lab; the authority projection
    // still reaches it from here, the way it reaches every other body.
    frontier.update(snapshot, connectionState, error);
    return loaded.module.ManifoldView(frontier.state);
  };

  /** What a lens's code says while it is fetched or after it failed; nothing once it is here. */
  const lensNotice = (id: LensId): TemplateInstance => {
    const loaded = lensCode();
    if (loaded.status === 'ready') return html``;
    return surfaceCodeBody(lensById(id).title, codeFailure(loaded));
  };

  /**
   * Home and Projects are the account's own list, which `/auth/session` already
   * holds: they draw it before any project is read, and whether the read ever
   * succeeds is not what they wait on.
   */
  const listsWithoutSnapshot = (path: string, route: ProjectRoute | null): boolean =>
    route === null && directory !== null && directory.length > 0 && (path === PRODUCT_HOME_PATH || path === PROJECTS_PATH);

  const pageBody = (path: string, route: ProjectRoute | null): TemplateInstance => {
    if (path === REPLAY_PATH) {
      const loaded = replayCode();
      if (loaded.status !== 'ready') return surfaceCodeBody('Replay', codeFailure(loaded));
      const receiptIdentity = new URLSearchParams(window.location.search).get('digest');
      return loaded.module.ConsumerReplay(replayView.status === 'idle' || replayView.receiptIdentity === receiptIdentity
        ? replayView : { status: 'idle' });
    }
    // An account that is a member of no project has nothing to read: every read would be refused `no_project`.
    if (route === null && directory !== null && directory.length === 0) {
      return NoProjects(NAV.find((item) => item.href === path)?.label ?? 'Matrix');
    }
    if (snapshot === null && listsWithoutSnapshot(path, route)) {
      const dataState = error !== null || unavailable ? 'unavailable' : 'pending';
      return path === PRODUCT_HOME_PATH
        ? ConsumerHome(null, connectionState, directory, dataState)
        : ConsumerProjects(null, connectionState, directory);
    }
    // The page's code is asked for with the projection, so the two arrive together.
    const accountPage = route === null && (path === SETTINGS_PATH || path === PROFILE_PATH) ? accountCode() : null;
    const tab = route === null ? null : projectTabs[route.tab]();
    if (!snapshot) return connectingBody();
    if (route === null || tab === null) {
      const shellRoute = SHELL_ROUTES[path];
      if (shellRoute) return shellRoute(snapshot, connectionState, directory);
      if (accountPage === null) return connectingBody();
      if (accountPage.status !== 'ready') {
        return surfaceCodeBody(NAV.find((item) => item.href === path)?.label ?? 'Matrix', codeFailure(accountPage));
      }
      return accountPage.module.ACCOUNT_ROUTES[path]?.(snapshot) ?? connectingBody();
    }
    const search = window.location.search;
    let codeView = NO_CODE_VIEW;
    if (route.tab === 'code') {
      const codeLoaded = codeTab();
      if (codeLoaded.status === 'ready') {
        code ??= codeLoaded.module.mountCode(surface, () => render());
        if (route.sub === null) code.sync(snapshot, search);
        codeView = code.view();
      }
    }
    const parts: ProjectPageParts = {
      route,
      snapshot,
      connection: connectionState,
      path,
      search,
      detail: detailView,
      code: codeView,
      manifold: manifoldBody,
      lensNotice,
    };
    // The frame is drawn at once; only the body below it waits for the tab's code.
    if (tab.status !== 'ready') {
      const label = PROJECT_TABS.find((item) => item.tab === route.tab)?.label ?? 'Project';
      return ProjectPage(parts, () => surfaceCodeBody(label, codeFailure(tab)), true);
    }
    return ProjectPage(parts, () => tab.module.projectTabBody(parts));
  };

  /** Re-mounts the lens when its host is new: first visit, another lens, or a frame that replaced its body. */
  const mountLensInto = (id: LensId): void => {
    const host = root.querySelector<HTMLElement>('[data-lens-host]');
    const loaded = lensCode();
    if (!host || loaded.status !== 'ready') return;
    if (lens && lensId === id && lensHost === host) return;
    lens?.dispose();
    lens = loaded.module.mountLens(host, surface, lensById(id));
    lensId = id;
    lensHost = host;
  };

  const releaseLens = (): void => {
    lens?.dispose();
    lens = undefined;
    lensId = undefined;
    lensHost = undefined;
  };

  const render = (): void => {
    if (disposed) return;
    const path = locationPathname();
    const search = window.location.search;
    publicSite?.release(path);
    const route = parseProjectPath(path);
    setCurrentProject(route?.project ?? snapshot?.product ?? viewedProject(null, directory, surface.project()));
    const wantedLens = lensForRoute(route, search);
    const manifold = showsManifold(route, search);
    if (wantedLens === null) releaseLens();
    if (!manifold) {
      frontier?.dispose();
      frontier = undefined;
    }
    if (!isKnownRoute(path)) {
      document.title = 'Not found · Matrix';
      mounted = reconcileRoot(
        root,
        mounted,
        snapshot === null
          ? NotFoundPage({ path })
          : Shell({
              path,
              snapshot,
              error,
              connectionState,
              account,
              directory,
              body: NotFoundBody({ path, signedIn: true }),
            }),
      );
      chrome.sync(root, path);
      return;
    }
    if (isPublicPath(path)) {
      const loaded = publicCode();
      if (loaded.status !== 'ready') {
        document.title = 'Matrix';
        mounted = reconcileRoot(
          root,
          mounted,
          html`<main class="mx-public-main" id="matrix-content" tabindex="-1">
            ${surfaceCodeBody('Matrix', codeFailure(loaded))}
          </main>`,
        );
        chrome.sync(root, path);
        return;
      }
      publicSite ??= loaded.module.createPublicSite(root, publicHost);
      if (sessionEnded) {
        sessionEnded = false;
        publicSite.sessionEnded();
      }
      mounted = reconcileRoot(root, mounted, publicSite.body(path, search));
      chrome.sync(root, path);
      publicSite.attach(path);
      return;
    }
    const tabLabel = PROJECT_TABS.find((item) => item.tab === route?.tab)?.label;
    const foreign = route !== null && snapshot !== null && route.project !== snapshot.product;
    document.title = foreign
      ? 'Not found · Matrix'
      : route
        ? `${changeIdFromPath(path) ? 'Change' : tabLabel} · ${route.project} · Matrix`
        : `${NAV.find((item) => item.href === path)?.label ?? 'Matrix'} · Matrix`;
    const dataUnavailable = snapshot === null && (error !== null || unavailable);
    mounted = reconcileRoot(
      root,
      mounted,
      Shell({
        path,
        snapshot,
        // The manifold owns its sources: a demo or a recording renders with
        // no authority at all, and the live source states its own disconnection.
        // Home and Projects list the account's projects without a read, and say
        // what is missing in place; a full-page notice would hide the list.
        error: manifold || listsWithoutSnapshot(path, route) ? null : dataUnavailable ? (error ?? UNAVAILABLE_DETAIL) : error,
        unavailable: dataUnavailable,
        connectionState,
        account,
        directory,
        body: pageBody(path, route),
      }),
    );
    chrome.sync(root, path);
    // The canvas and the lens host only exist once the shell has rendered them.
    if (manifold) frontier?.attach();
    if (wantedLens !== null) {
      mountLensInto(wantedLens);
      lens?.update(snapshot, connectionState, error);
    }
    observeGraph();
  };

  const loadReplayDetail = async (): Promise<void> => {
    if (locationPathname() !== REPLAY_PATH) {
      replayController?.abort();
      replayController = undefined;
      replayView = { status: 'idle' };
      return;
    }
    const receiptIdentity = new URLSearchParams(window.location.search).get('digest');
    if (!receiptIdentity) {
      replayController?.abort();
      replayController = undefined;
      replayView = { status: 'idle' };
      render();
      return;
    }
    if (replayView.status !== 'idle' && replayView.receiptIdentity === receiptIdentity) return;
    replayController?.abort();
    const controller = new AbortController();
    replayController = controller;
    replayView = { status: 'loading', receiptIdentity };
    render();
    try {
      const detail = await surface.fetchReplayDetail(receiptIdentity, controller.signal);
      if (disposed || controller.signal.aborted) return;
      replayView = { status: 'ready', receiptIdentity, detail };
    } catch (reason: unknown) {
      if (disposed || controller.signal.aborted) return;
      replayView = {
        status: reason instanceof SurfaceNotFoundError ? 'missing' : 'error', receiptIdentity,
        error: reason instanceof Error ? reason.message : 'Replay receipt is unreadable.',
      };
    } finally {
      if (replayController === controller) replayController = undefined;
    }
    render();
  };

  const loadChangeDetail = async (): Promise<void> => {
    // Both detail paths share navigation lifecycle, never snapshot receipt contents.
    void loadReplayDetail();
    if (!isChangeDetailPath(locationPathname())) {
      detailController?.abort();
      detailController = undefined;
      detailInFlightId = null;
      return;
    }
    const id = resolveInspectorChangeId(requestedChangeId(), snapshot?.recent_changes ?? []);
    if (!id) {
      detailView = idleChangeDetailView();
      render();
      return;
    }
    // A missing change stays missing until the projection lists it, so a
    // stream frame does not ask the authority the same question again.
    const knownMissing =
      detailView.status === 'missing' &&
      detailMissingId === id &&
      !(snapshot?.recent_changes ?? []).some((change) => change.id === id);
    if (
      knownMissing ||
      shouldSkipChangeDetailFetch(
        detailView.status,
        detailView.detail?.change.id,
        id,
        detailInFlightId,
      )
    ) {
      return;
    }
    detailController?.abort();
    const controller = new AbortController();
    detailController = controller;
    detailInFlightId = id;
    detailView = loadingChangeDetailView(detailView, id);
    render();
    try {
      const detail = await surface.fetchChangeDetail(id, controller.signal);
      if (disposed || controller.signal.aborted) return;
      detailView = { detail, status: 'ready', error: null };
    } catch (reason: unknown) {
      if (disposed || controller.signal.aborted) return;
      detailView = changeDetailViewFromFailure(reason);
      detailMissingId = detailView.status === 'missing' ? id : null;
    } finally {
      if (detailController === controller) {
        detailController = undefined;
        detailInFlightId = null;
      }
    }
    render();
  };

  const loadSnapshot = async (forced = false): Promise<void> => {
    /*
     * A read of this project that already failed is not asked again behind the
     * person's back: a refusal that repeats every second is a loop, and each
     * round reset "unavailable" to "reading". "Try again" is what asks again.
     */
    const key = surface.project() ?? '';
    if (!mayStartRead(readFailedFor, key, forced)) return;
    snapshotController?.abort();
    const controller = new AbortController();
    snapshotController = controller;
    const request = ++snapshotRequest;
    error = null;
    if (!snapshot) {
      // The bound runs from first paint; only a person asking again starts a fresh one.
      if (forced || unavailableTimer === undefined) armUnavailable();
      render();
    }
    try {
      const value = await surface.fetchSnapshot(controller.signal);
      if (disposed || request !== snapshotRequest) return;
      snapshot = value;
      readFailedFor = null;
      disarmUnavailable();
      streamRecoveryAttempt = 0;
      if (connectionState === 'connecting') connectionState = 'live';
      if (!stopSnapshots) startSnapshotStream();
      render();
      void loadChangeDetail();
    } catch (reason: unknown) {
      // An aborted read was superseded (by a newer read or a fresher stream
      // frame); it did not fail.
      if (disposed || request !== snapshotRequest || controller.signal.aborted) return;
      /*
       * A refresh that fails while a projection is on screen is not nothing.
       * This used to return on `snapshot` being set, which swallowed every
       * later failure: a session that expired mid-visit was never noticed, so
       * the operator kept reading authority state they were no longer
       * authorized for, and no indicator moved.
       *
       * The credential branch below runs regardless, because expiry is exactly
       * the case where a snapshot is already held. Any other failure marks the
       * connection reconnecting rather than setting `error` — the shell swaps
       * the whole body for the error panel, and blanking a good projection
       * because one refresh failed loses more than it reports.
       */
      const action = snapshotFailureAction(reason, snapshot !== null);
      if (action === 'reconnect') {
        // Keep the projection on screen; the shell swaps the whole body for
        // the error panel, and blanking good data because one refresh failed
        // loses more than it reports.
        connectionState = 'reconnecting';
        if (!stopSnapshots) scheduleStreamRecovery();
        render();
        return;
      }
      if (action === 'authenticate' && reason instanceof SurfaceAuthenticationError) {
        const path = locationPathname();
        /*
         * An account page is not somewhere to be sent away from. A snapshot
         * read that resolves while a person is on /verify or /signup used to
         * rewrite the address to /signin, so a sign-up landed on "Welcome
         * back" with the verification link it had just been handed discarded.
         * A public path is already the right place to ask for a session.
         */
        if (isPublicPath(path)) {
          render();
          return;
        }
        // An address Matrix does not serve is not found whoever asks; asking
        // for a session first would promise a page that does not exist.
        if (!isKnownRoute(path)) {
          render();
          return;
        }
        const destination = `${path}${window.location.search}`;
        window.history.replaceState({}, '', `${SIGN_IN_PATH}?next=${encodeURIComponent(destination)}`);
        // Being moved to a sign-in form is an event, and it was rendered as
        // one that had not happened: the card came up bare, so a person whose
        // session had expired read it as their click doing nothing.
        sessionEnded = true;
        render();
        return;
      }
      readFailedFor = key;
      error = surfaceUnreachableMessage(reason);
      render();
    } finally {
      if (snapshotController === controller) snapshotController = undefined;
    }
  };

  const startSnapshotStream = (): void => {
    stopSnapshots?.();
    stopSnapshots = surface.subscribeSnapshots(
      (value) => {
        if (disposed) return;
        // Supersede any in-flight GET /state so its abort is not read as a
        // refresh failure.
        snapshotRequest += 1;
        snapshotController?.abort();
        streamRecoveryAttempt = 0;
        snapshot = value;
        error = null;
        disarmUnavailable();
        render();
        void loadChangeDetail();
      },
      (state) => {
        if (disposed) return;
        connectionState = state;
        render();
      },
      () => {
        // The browser gave up on the stream. Reading the state directly both
        // restarts it on success and routes an expired session to sign-in.
        stopSnapshots?.();
        stopSnapshots = undefined;
        scheduleStreamRecovery();
      },
    );
  };

  const scheduleStreamRecovery = (): void => {
    if (disposed || streamRecoveryTimer !== undefined) return;
    const delay = streamRecoveryDelay(streamRecoveryAttempt);
    streamRecoveryAttempt += 1;
    streamRecoveryTimer = setTimeout(() => {
      streamRecoveryTimer = undefined;
      if (disposed || stopSnapshots) return;
      void loadSnapshot();
    }, delay);
  };

  /**
   * A signed-in person is about to open a project: fetch the pages most often
   * opened first while the browser is idle, so the click finds them here.
   * `import()` rather than a `lazyModule` ask, which would redraw the page for
   * code nothing on it shows yet. Skipped where the person asked to save data.
   */
  let prefetched = false;
  const prefetchProjectPages = async (): Promise<void> => {
    const saveData =
      'connection' in navigator &&
      typeof navigator.connection === 'object' &&
      navigator.connection !== null &&
      'saveData' in navigator.connection &&
      navigator.connection.saveData === true;
    if (prefetched || saveData) return;
    prefetched = true;
    const idle = (): Promise<void> =>
      new Promise((resolve) => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(() => resolve(), { timeout: 5_000 });
        else setTimeout(resolve, 1_000);
      });
    // One chunk per idle slice: a second one is a second task, never one long one.
    for (const load of [() => import('./tab-overview.js'), () => import('./tab-changes.js')]) {
      await idle();
      if (disposed) return;
      try {
        await load();
      } catch {
        // A missed head start, nothing more: the visit asks for the chunk again and says what happened.
        return;
      }
    }
  };

  /**
   * Ask the Worker who this browser is.
   *
   * `GET /auth/session` has been served since the account service landed and
   * nothing had ever called it, so the header named the authority's most
   * recent producer -- an agent id -- where the signed-in person's name
   * belongs. A failure here is not worth a notice: the header falls back to
   * the producer, which is what it showed before.
   */
  const loadAccount = async (): Promise<void> => {
    try {
      const response = await accountRequest('/auth/session', undefined, 'GET');
      if (response.ok) {
        const body: unknown = await response.json();
        account = signedInAccount(body);
        site = siteAccounts(body) ?? site;
        /*
         * A signed-out body lists no projects; that is no session, not an
         * account that is a member of nothing, and the read that follows is
         * what sends the person to sign in.
         */
        const memberships = projectMemberships(body);
        directory = account !== null || (memberships?.length ?? 0) > 0 ? memberships : null;
        if (account !== null && directory !== null) writeSessionCache({ account, projects: directory });
        else clearSessionCache();
      }
    } catch {
      // The origin lists no projects, so it chooses which one is read.
    }
    sessionAnswered = true;
    syncSurfaceProject();
    render();
    if (account !== null && !isPublicPath(locationPathname())) void prefetchProjectPages();
  };

  /**
   * Point the surface client at the project in view, and read it.
   *
   * A `/p/<project>/…` address names the project; elsewhere the session's
   * memberships do (`viewedProject`). Moving to another project drops what was
   * read for the last one -- its snapshot, stream, open change and lens -- so
   * nothing of one project is ever drawn under another's name, and every read
   * after it names the new project as `?project=`.
   */
  const syncSurfaceProject = (): void => {
    const path = locationPathname();
    if (disposed || isPublicPath(path)) return;
    const route = parseProjectPath(path);
    if (route === null && !sessionAnswered && directory === null) return;
    const wanted = viewedProject(route?.project ?? null, directory, surface.project());
    if (wanted !== surface.project()) {
      surface.selectProject(wanted);
      snapshotRequest += 1;
      snapshotController?.abort();
      stopSnapshots?.();
      stopSnapshots = undefined;
      snapshot = null;
      error = null;
      readFailedFor = null;
      detailView = idleChangeDetailView();
      detailMissingId = null;
      releaseLens();
    }
    // A member of no project: there is nothing this account may read.
    if (wanted === null && directory !== null) return;
    if (stopSnapshots || snapshotController) return;
    connectionState = 'connecting';
    void loadSnapshot();
  };

  /**
   * End the session the shell is showing.
   *
   * The Worker has answered `POST /auth/signout` since the account service
   * landed; nothing in the client ever called it, so signing out meant
   * clearing a cookie by hand. The reload is what makes the sign-out visible:
   * the surface state in memory was fetched as this person, and the public
   * pages are served on a fresh request.
   */
  const signOut = async (): Promise<void> => {
    try {
      const response = await accountRequest('/auth/signout');
      if (!response.ok) {
        toast('Could not sign out. Try again in a moment.', 'error');
        return;
      }
    } catch (reason: unknown) {
      toast(accountUnreachableMessage(reason), 'error');
      return;
    }
    clearSessionCache();
    location.assign('/signin');
  };

  /** The password strength readout belongs to the account forms, which are the public site's. */
  const onInput = (event: Event): void => {
    publicSite?.onInput(event);
  };

  const onSubmit = (event: Event): void => {
    publicSite?.onSubmit(event);
  };

  const onClick = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (code?.action(target)) {
      event.preventDefault();
      return;
    }
    if (publicSite?.onClick(target)) {
      event.preventDefault();
      return;
    }

    const retry = target.closest<HTMLElement>('[data-action="retry"]');
    if (retry) {
      event.preventDefault();
      void loadSnapshot(true);
      return;
    }

    // A surface whose code failed to load: a fresh document names the current chunks.
    if (target.closest('[data-action="reload"]')) {
      event.preventDefault();
      window.location.reload();
      return;
    }

    const paletteTrigger = target.closest<HTMLElement>('[data-action="palette"]');
    if (paletteTrigger) {
      event.preventDefault();
      void openPalette();
      return;
    }

    if (graphCanvas?.onClick(event, target)) return;

    const link = target.closest('a[data-nav]');
    if (
      !(link instanceof HTMLAnchorElement) ||
      !(event instanceof MouseEvent) ||
      !shouldInterceptNavigation(event)
    ) {
      return;
    }
    event.preventDefault();
    navigate(link.getAttribute('href') ?? '/');
  };

  const onPopState = (): void => {
    afterLocationChange();
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (isPublicPath(locationPathname())) return;
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
    if (event.repeat) return;
    event.preventDefault();
    void openPalette();
  };

  root.addEventListener('click', onClick);
  root.addEventListener('input', onInput);
  root.addEventListener('submit', onSubmit);
  window.addEventListener('popstate', onPopState);
  window.addEventListener('keydown', onKeyDown);
  // Every page asks who this is: a public page needs to know whether sign-up
  // takes an invite and whether a reset link could ever arrive.
  if (!isPublicPath(locationPathname())) {
    // The last answer paints the account, the switcher and the list at once; `/auth/session` revalidates it.
    const cached = readSessionCache();
    if (cached !== null) {
      account = cached.account;
      directory = cached.projects;
    }
    // The wait for project data is bounded from this first paint, not from whenever the read starts.
    armUnavailable();
  }
  void loadAccount();
  if (!isPublicPath(locationPathname())) {
    syncSurfaceProject();
    void loadChangeDetail();
  }

  render();
  return () => {
    disposed = true;
    clearTimeout(streamRecoveryTimer);
    window.clearTimeout(unavailableTimer);
    snapshotController?.abort();
    replayController?.abort();
    detailController?.abort();
    stopSnapshots?.();
    graphCanvas?.dispose();
    root.removeEventListener('click', onClick);
    root.removeEventListener('input', onInput);
    root.removeEventListener('submit', onSubmit);
    code?.dispose();
    window.removeEventListener('popstate', onPopState);
    window.removeEventListener('keydown', onKeyDown);
    mounted?.unmount();
    frontier?.dispose();
    publicSite?.dispose();
    lens?.dispose();
    chrome.dispose();
    palette?.dispose();
  };
}
